import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Modal } from 'antd'
import { constants } from '../../constants'

// Read-only "what the patient sees" instructions view for an exercise, rendered
// as a modal for clinicians. Mirrors the patient app's exercise details/tutorial
// view (physio_app_web/components/Exercises/exercise.view.js) — same description
// steps and same media (video → image sequence → single image with GIF
// preference). The one deliberate difference: hold time and number of sets are
// per-patient-schedule values that don't exist on a master exercise, so they are
// omitted here (as are the schedule-only "repeat" / "times per day" lines).

// Stored media is a /content-relative path; prefix the API origin to render it.
// (Matches toMediaUrl in the exercise library page.)
const toMediaUrl = (p) => {
  if (!p || p === 'NA') return ''
  if (/^https?:\/\//.test(p)) return p
  return `${constants.API_URL}${p.startsWith('/') ? '' : '/'}${p}`
}

// Prefer an ANIMATED sibling sharing the image's base name (e.g.
// `/uploads/x.png` → `/uploads/x.gif`); fall back to the static image once every
// animated probe 404s. Mirrors the patient view's tutorialCandidates (in
// physio_app_web/components/Exercises/exercise.view.js) so the illustration
// animates identically — including the extension order, which keeps `.gif`
// first (every clip shipped before Heel Slide 2) and `.webp` second (what the
// newer artwork ships as). Keep the two lists in step.
const TUTORIAL_EXTS = ['gif', 'webp']

const tutorialCandidates = (imagePath) => {
  if (!imagePath) return []
  // Already animated — take it as-is rather than deriving a missing sibling.
  if (/\.(gif|webp)(\?|$)/i.test(imagePath)) return [imagePath]
  const swap = (ext) =>
    /\.[a-z0-9]+(\?|$)/i.test(imagePath)
      ? imagePath.replace(/\.[a-z0-9]+(\?|$)/i, `.${ext}$1`)
      : `${imagePath}.${ext}`
  return TUTORIAL_EXTS.map(swap).concat([imagePath])
}

function ExerciseInstructionsModal(props) {
  const { visible, onClose, exercise } = props
  const ex = exercise || {}

  // Animated-vs-static fallback for the single-image illustration: each 404
  // advances one place down the candidate list, which ends on the static image.
  // Reset whenever the exercise (image) changes so each one probes from the top.
  const [tutorialIdx, setTutorialIdx] = useState(0)
  useEffect(() => {
    setTutorialIdx(0)
  }, [ex.image])

  // Split the description into one bullet per step. The patient app only splits
  // on ';', but many exercises store a single sentence-separated string with no
  // semicolons, which would render as one long bullet — so we also break on
  // sentence-ending periods (period followed by whitespace). Decimals like
  // "1.5" have no trailing space, so they're left intact. Each step's trailing
  // period is stripped, then re-added below, to normalise spacing.
  const steps = String(ex.description || '')
    .split(/[;.]\s+/)
    .map((s) => s.trim().replace(/\.+$/, ''))
    .filter(Boolean)

  const hasVideo = ex.video && ex.video !== 'NA'
  const hasSequence = Array.isArray(ex.imagesequence) && ex.imagesequence.length >= 2

  const renderMedia = () => {
    if (hasVideo) {
      return (
        <video
          src={toMediaUrl(ex.video)}
          controls
          playsInline
          style={{ width: '100%', borderRadius: 10, background: '#000' }}
        />
      )
    }
    if (hasSequence) {
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }}>
          {ex.imagesequence.map((img, idx) => (
            <div key={idx} style={{ position: 'relative' }}>
              <span
                style={{
                  position: 'absolute',
                  top: 6,
                  left: 6,
                  minWidth: 22,
                  height: 22,
                  padding: '0 6px',
                  borderRadius: 11,
                  background: 'rgba(0,20,41,0.85)',
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {idx + 1}
              </span>
              <img
                src={toMediaUrl(img)}
                alt={`${ex.name || 'Exercise'} step ${idx + 1}`}
                style={{
                  width: 180,
                  height: 180,
                  objectFit: 'cover',
                  borderRadius: 10,
                  border: '1px solid #e5e7eb',
                }}
              />
            </div>
          ))}
        </div>
      )
    }
    // No dedicated video / image-sequence: fall back to the single image field.
    // Mirrors the patient view — an image field that is itself a video plays
    // (animated like the old GIF); otherwise we prefer an animated sibling
    // sharing the image's base name (.gif, then .webp) and fall back to the
    // static image once both 404.
    const baseImg = ex.image || '/exdefault.png'
    if (/\.(mp4|m4v|webm|mov)(\?|$)/i.test(baseImg)) {
      return (
        <video
          src={toMediaUrl(baseImg)}
          autoPlay
          muted
          loop
          playsInline
          style={{
            maxWidth: '100%',
            borderRadius: 10,
            boxShadow: '4px 10px 14px 4px rgba(0, 0, 0, 0.35)',
          }}
        />
      )
    }
    const candidates = tutorialCandidates(baseImg)
    const idx = Math.min(tutorialIdx, candidates.length - 1)
    // Anything but the last candidate can still fall through on 404.
    const canFallBack = idx < candidates.length - 1
    return (
      <img
        src={toMediaUrl(candidates[idx] || baseImg)}
        alt={ex.name || 'Exercise'}
        style={{
          maxWidth: '100%',
          borderRadius: 10,
          boxShadow: '4px 10px 14px 4px rgba(0, 0, 0, 0.35)',
          // Tint the card behind the illustration to match the patient app's
          // tutorial view (physio_app_web/components/Exercises/exercise.view.js).
          // It only shows through on transparent PNGs/GIFs (the newer figures);
          // opaque artwork covers it completely.
          backgroundColor: '#fee4a8',
        }}
        onError={canFallBack ? () => setTutorialIdx(idx + 1) : undefined}
      />
    )
  }

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={640}
      wrapClassName='ex-navy-modal'
      title={<span style={{ color: '#001429', fontWeight: 700 }}>{ex.name || 'Exercise'}</span>}
    >
      {steps.length > 0 && (
        <ul style={{ paddingLeft: 20, marginBottom: 20, color: '#334155', lineHeight: 1.7 }}>
          {steps.map((step, idx) => (
            <li key={idx}>{step}.</li>
          ))}
        </ul>
      )}
      <div style={{ textAlign: 'center' }}>{renderMedia()}</div>
    </Modal>
  )
}

ExerciseInstructionsModal.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  exercise: PropTypes.object,
}

export default ExerciseInstructionsModal
